import { motion } from 'framer-motion';
import { Smartphone } from 'lucide-react';
import { fadeInUp, stagger } from './data';

export const ComingSoonSection = () => (
  <section id="coming-soon" aria-label="Coming Soon - Mobile Apps" className="py-20 md:py-32" data-testid="coming-soon-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        className="text-center mb-16"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
      >
        <motion.p variants={fadeInUp} className="text-amber-400 font-semibold mb-4 tracking-wider uppercase text-sm">
          Coming Soon
        </motion.p>
        <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-bold mb-6 text-white">
          SlotFleet in Your Pocket
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-lg text-gray-400 max-w-2xl mx-auto">
          Manage your entire fleet on the go. Native mobile apps for iOS and Android are on the way.
        </motion.p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* iOS Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="relative group">
          <div className="bg-neutral-900 border border-white/5 rounded-2xl p-8 hover:border-amber-500/30 transition-all duration-300 h-full">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center mb-6 border border-white/10">
              <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-white" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-3">iOS App</h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              A native iPhone and iPad experience. Get real-time push notifications, use your camera to document machines, and access GPS navigation to your locations — all from the App Store.
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {['Push Notifications', 'Camera', 'GPS Navigation', 'Offline Access'].map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-white/5 text-gray-300 text-xs font-medium border border-white/10">{tag}</span>
              ))}
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              Coming to App Store
            </div>
          </div>
        </motion.div>

        {/* Android Card */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} transition={{ delay: 0.15 }} className="relative group">
          <div className="bg-neutral-900 border border-white/5 rounded-2xl p-8 hover:border-amber-500/30 transition-all duration-300 h-full">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-800 to-green-950 flex items-center justify-center mb-6 border border-white/10">
              <Smartphone className="w-8 h-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Android App</h3>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Full-featured Android app for phones and tablets. Instant push alerts for service calls, snap photos of machine issues, and navigate routes with GPS — straight from Google Play.
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              {['Push Notifications', 'Camera', 'GPS Navigation', 'Material Design'].map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-white/5 text-gray-300 text-xs font-medium border border-white/10">{tag}</span>
              ))}
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              Coming to Google Play
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
